import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  ViewChild
} from '@angular/core';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {ResearchPaperDTO} from '../../dto/research-paper';
import {startPaperResearch} from '../../services/research-paper/research-paper.actions';
import {
  selectLoading,
  selectPapersByProjectId,
  selectPapersSearched
} from '../../services/research-paper/research-paper.selectors';
import {ResearchPaperCardComponent} from '../research-paper-card/research-paper-card.component';
import {MatChipSelectionChange, MatChipsModule} from '@angular/material/chips';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {MatBadgeModule} from '@angular/material/badge';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../../utils/components/confirm-dialog/confirm-dialog.component';
import {ResearchPaperSmartSearchComponent} from '../research-paper-smart-search/research-paper-smart-search.component';
import {createFilterBlocks, filterPaper} from '../../helper/research-paper-filter';
import {CUSTOM_FILTER, FilterBlock} from '../../models/filter';
import {selectCriteriaByProjectId} from '../../services/screening-criteria/screening-criteria.selectors';
import {ScreeningCriteriaDTO} from '../../dto/screening-criteria';
import {MatCheckboxChange, MatCheckboxModule} from '@angular/material/checkbox';
import {SelectionModel} from '@angular/cdk/collections';
import {hasPubmedAPIKey, hasScopusAPIKey, hasSemanticAPIKey} from '../../../../utils/models/llm-setting';
import {
  LlmModelSettingDialogComponent
} from '../../../../utils/components/llm-model-setting-dialog/llm-model-setting-dialog.component';
import {ProjectPermissionService} from '../../../../utils/services/project-permission.service';
import {LoadingCardComponent} from '../../../../utils/components/loading-card/loading-card.component';
import {EmptyStateComponent} from '../../../../utils/components/empty-state/empty-state.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-research-paper-list',
  imports: [
    ResearchPaperCardComponent,
    MatInputModule,
    MatChipsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatBadgeModule,
    MatCheckboxModule,
    ResearchPaperSmartSearchComponent,
    LoadingCardComponent,
    EmptyStateComponent
  ],
  templateUrl: './research-paper-list.component.html',
  styleUrl: './research-paper-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResearchPaperListComponent {
  private readonly store: Store = inject(Store);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly projectPermissionService: ProjectPermissionService = inject(ProjectPermissionService);
  readonly projectId = input<number>();
  readonly filters = input<string[]>([
    CUSTOM_FILTER.ALL_IDENTIFIED_ARTICLES,
    CUSTOM_FILTER.PDF_DOWNLOADED,
    CUSTOM_FILTER.TITLE_ABSTRACT_SCREENING_AUDITED,
    CUSTOM_FILTER.FULL_TEXT_SCREENING_AUDITED,
    CUSTOM_FILTER.TITLE_ABSTRACT_SCREENING_PASSED,
    CUSTOM_FILTER.FULL_TEXT_SCREENING_PASSED,
    CUSTOM_FILTER.MANUAL_OVERWRITTEN]);
  readonly papersFilter = input<number[]>();
  readonly disablePapersDialog = input<boolean>(false);
  readonly isSelectList = input<boolean>(false);
  readonly fixedAppliedFilters = input<string[]>();
  readonly linkSelectedTab = input<number>(0);

  readonly itemClicked = output<ResearchPaperDTO>();
  readonly itemsSelected = output<ResearchPaperDTO[]>();

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(ResearchPaperSmartSearchComponent) smartSearch?: ResearchPaperSmartSearchComponent;

  selection = new SelectionModel<ResearchPaperDTO>(true, []);

  readonly criteria = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectCriteriaByProjectId(projectId)) : of([] as ScreeningCriteriaDTO[]))
    ),
    {initialValue: [] as ScreeningCriteriaDTO[]}
  );

  selectedFilter: boolean[] = [false, false];
  smartSearchFilter: FilterBlock[] = [];
  searchValue: string = '';

  papers: ResearchPaperDTO[] = [];
  readonly loading = this.store.selectSignal(selectLoading());
  private readonly storePapers = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPapersByProjectId(projectId)) : of([] as ResearchPaperDTO[]))
    ),
    {initialValue: [] as ResearchPaperDTO[]}
  );
  filteredPapers: ResearchPaperDTO[] = [];

  pageSize = 10;
  pageIndex = 0;
  paginatedPapers: ResearchPaperDTO[] = [];

  readonly editMode = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => this.projectPermissionService.hasWritePermissionViaStore(projectId))
    ),
    {initialValue: false}
  );

  readonly alreadySearched = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPapersSearched(projectId)) : of(false))
    ),
    {initialValue: false}
  );

  constructor() {
    effect(() => {
      let papers = this.storePapers();
      if (this.papersFilter() && this.papersFilter()!.length > 0) {
        papers = papers.filter(paper => this.papersFilter()?.includes(paper.id));
      }
      if (this.fixedAppliedFilters() && this.fixedAppliedFilters()!.length > 0) {
        let createdfilters = createFilterBlocks(this.fixedAppliedFilters()!);
        papers = papers.filter(paper => {
          const passesAllSelectedFilters = createdfilters.every(filter => filterPaper(paper, filter));
          return createdfilters.length === 0 || passesAllSelectedFilters;
        });
      }
      this.papers = papers;
      this.filterAndSearchPapers();
    });
  }

  applySearch(value: string) {
    this.searchValue = value.toLowerCase();
    this.filterAndSearchPapers();
  }

  handlePageEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updatePaginatedPapers();
  }

  changeFilter(filters: FilterBlock[]) {
    this.smartSearchFilter = filters;
    this.filterAndSearchPapers();
  }

  applyFilter(ev: MatChipSelectionChange, index: number) {
    if (ev.selected && index === 0) { // select all identified articles
      this.selectedFilter = this.filters().map(() => false);
      this.selectedFilter[index] = true;
    } else {
      this.selectedFilter[index] = ev.selected;
    }
    this.filterAndSearchPapers();
  }

  getBadgeCount(filter: string): number {
    if (filter === CUSTOM_FILTER.ALL_IDENTIFIED_ARTICLES) {
      return this.papers.length;
    }
    let createdfilters = createFilterBlocks([filter]);
    return this.papers.filter(paper => {
      const passesAllSelectedFilters = createdfilters.every(filter => filterPaper(paper, filter));
      return createdfilters.length === 0 || passesAllSelectedFilters;
    }).length;
  }


  private filterAndSearchPapers() {
    const selectedFilter = this.filters().filter((_, index) => this.selectedFilter[index]);
    const allFilter = selectedFilter.includes(CUSTOM_FILTER.ALL_IDENTIFIED_ARTICLES);
    let createdfilters = createFilterBlocks(selectedFilter);
    createdfilters = createdfilters.concat(this.smartSearchFilter);
    this.filteredPapers = this.papers.filter(paper => {
      if (!paper || !paper.title) {
        return false;
      }
      if (!this.paperContainsSearchText(paper, this.searchValue)) {
        return false;
      }
      if (allFilter) {
        return true;
      }
      const passesAllSelectedFilters = createdfilters.every(filter => filterPaper(paper, filter));
      return createdfilters.length === 0 || passesAllSelectedFilters;
    });
    this.updatePaginatedPapers();
  }

  private paperContainsSearchText(paper: ResearchPaperDTO, searchText: string): boolean {
    if (!searchText) return true;

    // Check all searchable text fields
    return (
      (paper.title?.toLowerCase().includes(searchText) || false) ||
      (paper.abstractText?.toLowerCase().includes(searchText) || false) ||
      (paper.authors?.toLowerCase().includes(searchText) || false) ||
      (paper.venue?.toLowerCase().includes(searchText) || false) ||
      (paper.doi?.toLowerCase().includes(searchText) || false) ||
      (paper.semanticScholarId?.toLowerCase().includes(searchText) || false) ||
      (paper.magId?.toLowerCase().includes(searchText) || false) ||
      (paper.pubMedId?.toLowerCase().includes(searchText) || false) ||
      (paper.corpusId?.toLowerCase().includes(searchText) || false)
    );
  }

  private updatePaginatedPapers() {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedPapers = this.filteredPapers.slice(startIndex, endIndex);
  }

  startResearch(): void {
    if (!this.projectId()) {
      console.log('No project id');
      return;
    }

    let warningMessage: string = '';
    if (!hasPubmedAPIKey()) {
      warningMessage += "You don't provide an PubMed Api Key (optional).<br>"
    }
    if (!hasSemanticAPIKey()) {
      warningMessage += "You don't provide an Semantic Scholar Api Key  (optional).<br>"
    }
    if (!hasScopusAPIKey()) {
      warningMessage += "You don't provide an Scopus Api Key."
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Search for papers online',
        message: 'We will search online for papers based on your search strategy. <br>' +
          '  If you you want to revise the search-query, click at clicking on the settings icon next to the navigation item <br>' +
          'Iif you want to start the search, which can take a while click here?',
        warningMessage: warningMessage,
        warningExtendingText: "You have not optimal settings",
        warningButtonText: "Go to your settings",
        warningButtonClick: () => this.dialog.open(LlmModelSettingDialogComponent, {
          autoFocus: false,
          height: '600px',
          width: '600px',
          maxWidth: '100vw'
        }),
        confirmButtonText: 'Research',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.store.dispatch(startPaperResearch({projectId: this.projectId()!}));
    });

  }

  clickItem(paper: ResearchPaperDTO) {
    this.itemClicked.emit(paper);
  }

  resetFilters(): void {
    this.searchValue = '';
    this.selectedFilter = this.selectedFilter.map(() => false);

    this.changeFilter([]);

    this.pageIndex = 0;
    if (this.smartSearch) {
      this.smartSearch.resetFilters();
    }
    this.updatePaginatedPapers();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.filteredPapers.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.filteredPapers);
    this.itemsSelected.emit(this.selection.selected);

  }

  selectPaper($event: MatCheckboxChange, paper: ResearchPaperDTO) {
    $event ? this.selection.toggle(paper) : null
    this.itemsSelected.emit(this.selection.selected);
  }


}
